/**
 * OrganizationSection — Corporate user's organization at a glance.
 *
 * Shows designation, company summary with link to full company record,
 * and team management pointer. Intentionally light - heavy company data
 * lives in the company management area.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/constants';
import { EditableFieldsCard, ProfileCard, Pill, FieldRow } from './ProfilePrimitives';
import Button from '../common/Button';
import { companiesAPI, professionalAssignmentAPI } from '../../services/api';

const MUTED = '#7a7060';

export default function OrganizationSection({ user }) {
  const { updateProfile } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [teamCount, setTeamCount] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch company and team data. The two fetches are isolated so one failing
  // doesn't blank the other's card.
  useEffect(() => {
    const fetchOrgData = async () => {
      setLoading(true);

      try {
        // GET /companies returns the array directly in `data`.
        const companiesResponse = await companiesAPI.getAll();
        if (companiesResponse.success) {
          const list = Array.isArray(companiesResponse.data)
            ? companiesResponse.data
            : companiesResponse.data?.companies || [];
          setCompanies(list);
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }

      try {
        // Professionals assigned by this corporate (active assignments only).
        const assignmentsResponse = await professionalAssignmentAPI.getMyAssignments();
        if (assignmentsResponse.success) {
          const activeAssignments = assignmentsResponse.data?.assignments?.filter(
            (assignment) => assignment.status === 'active'
          ) || [];
          setTeamCount(activeAssignments.length);
        }
      } catch (error) {
        console.error('Failed to fetch team assignments:', error);
      }

      setLoading(false);
    };

    if (user?.roleName === 'corporate') {
      fetchOrgData();
    }
    // Key on the id, not the object — refreshUser() swaps the user object
    // identity on every profile load and would refetch 3-4x otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.roleName]);

  // Get primary company (first one for now)
  const primaryCompany = companies[0];

  const organizationFields = [
    {
      name: 'designation',
      label: 'Your Designation',
      placeholder: 'e.g., Director, Company Secretary, Manager',
      required: true,
    },
  ];

  // EditableFieldsCard treats a non-throwing onSave as success — rethrow so a
  // failed save keeps the card in edit mode with the error shown, instead of
  // silently closing with stale data.
  const handleSaveOrganization = async (updates) => {
    await updateProfile(updates);
  };

  const goToManageCollaborators = () => {
    navigate(ROUTES.MANAGE_COLLABORATORS);
  };

  // CompaniesPage reads location.state: selectedCin scrolls to + pins the
  // company; openAddCompany opens the Add Company modal directly.
  const goToCompanyDetails = (cin) => {
    navigate(ROUTES.COMPANIES, { state: { selectedCin: cin } });
  };

  const goToAddCompany = () => {
    navigate(ROUTES.COMPANIES, { state: { openAddCompany: true } });
  };

  return (
    <div className="space-y-6">
      {/* Designation Card */}
      <EditableFieldsCard
        title="Your Role"
        subtitle="Your designation within the organization"
        icon="👔"
        fields={organizationFields}
        values={{
          designation: user?.designation || '',
        }}
        onSave={handleSaveOrganization}
      />

      {/* Company Summary Card */}
      <ProfileCard
        title="Organization"
        subtitle="Company details and full record"
        icon="🏢"
      >
        <div className="space-y-4">
          {loading ? (
            <div className="text-[12.5px]" style={{ color: MUTED }}>
              Loading company information...
            </div>
          ) : companies.length > 0 ? (
            <>
              {/* Primary Company Info */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#f8f5ef' }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-[14px]" style={{ color: '#08111f' }}>
                      {primaryCompany.name}
                    </h4>
                    <div className="text-[11.5px]" style={{ color: MUTED }}>
                      CIN: {primaryCompany.cin}
                    </div>
                  </div>
                  <Pill tone="green">
                    Primary Company
                  </Pill>
                </div>

                {/* Company Summary Fields */}
                <div className="grid grid-cols-2 gap-3 text-[11.5px]">
                  <div>
                    <div style={{ color: MUTED }}>Company Type</div>
                    <div className="font-medium" style={{ color: '#08111f' }}>
                      {primaryCompany.type || 'Not specified'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: MUTED }}>State</div>
                    <div className="font-medium" style={{ color: '#08111f' }}>
                      {primaryCompany.state || 'Not specified'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: MUTED }}>Incorporation Date</div>
                    <div className="font-medium" style={{ color: '#08111f' }}>
                      {primaryCompany.incorporated
                        ? new Date(primaryCompany.incorporated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'Not specified'
                      }
                    </div>
                  </div>
                  <div>
                    <div style={{ color: MUTED }}>Status</div>
                    <div className="font-medium">
                      <Pill tone={primaryCompany.status === 'Active' ? 'green' : 'neutral'}>
                        {primaryCompany.status || 'Unknown'}
                      </Pill>
                    </div>
                  </div>
                </div>

                {/* Link to Full Company Record */}
                <div className="mt-3 pt-3 border-t" style={{ borderColor: '#ddd6c6' }}>
                  <button
                    onClick={() => goToCompanyDetails(primaryCompany.cin)}
                    className="text-[11px] font-medium transition-colors"
                    style={{ color: '#145f5f' }}
                    onMouseOver={(e) => { e.currentTarget.style.color = '#0d4f4f'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = '#145f5f'; }}
                  >
                    → View Full Company Record
                  </button>
                </div>
              </div>

              {/* Multiple Companies Indicator */}
              {companies.length > 1 && (
                <div className="text-[11.5px]" style={{ color: MUTED }}>
                  +{companies.length - 1} more company/companies associated with your account
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <div className="text-[12.5px]" style={{ color: MUTED }}>
                No companies associated with your account yet.
              </div>
              <Button
                variant="primary"
                size="sm"
                className="mt-2"
                onClick={goToAddCompany}
              >
                Add Company
              </Button>
            </div>
          )}
        </div>
      </ProfileCard>

      {/* Team Management Card */}
      <ProfileCard
        title="Team"
        subtitle="Manage professionals and access permissions"
        icon="👥"
        action={
          <Button variant="outline" size="sm" onClick={goToManageCollaborators}>
            Manage Team
          </Button>
        }
      >
        <div className="space-y-4">
          {/* Team Statistics */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[12.5px]" style={{ color: MUTED }}>
                Active Professionals
              </div>
              <div className="text-[18px] font-semibold" style={{ color: '#08111f' }}>
                {loading ? '...' : teamCount || 0}
              </div>
            </div>
            <div className="text-right">
              {loading ? (
                <div className="text-[11px]" style={{ color: MUTED }}>
                  Loading...
                </div>
              ) : teamCount > 0 ? (
                <Pill tone="green">
                  Team Active
                </Pill>
              ) : (
                <Pill tone="neutral">
                  No Team Members
                </Pill>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="primary" size="sm" onClick={goToManageCollaborators}>
              View All Professionals
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.MANAGE_COLLABORATORS, { state: { openCreate: true } })}
            >
              Invite New Professional
            </Button>
          </div>

          {/* Team Management Description */}
          <div className="text-[11px]" style={{ color: MUTED }}>
            Manage professional access, assignments, and permissions for your organization. 
            Invite new professionals and control their access to company data.
          </div>
        </div>
      </ProfileCard>
    </div>
  );
}
